import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { CreateIntegratorRequest, Integrator, Invitation } from '../types';
import { integratorApi } from '../api/integrators';
import { useAuth } from '../context/AuthContext';
import IntegratorAutocomplete, { integratorLabel } from '../components/IntegratorAutocomplete';
import { Toast, useToast } from '../components/Toast';
import { isValidNip, isValidPostCode, nipDigits } from '../utils/validation';

const PAGE_SIZE = 10;

const statusBadge: Record<string, string> = {
  PENDING: 'badge badge-warning',
  ACCEPTED: 'badge badge-success',
  EXPIRED: 'badge badge-danger',
};
const statusLabel: Record<string, string> = {
  PENDING: 'Oczekuje',
  ACCEPTED: 'Aktywowane',
  EXPIRED: 'Wygasłe',
};

const addressLabel = (i: Integrator) => {
  if (!i.billingStreet) return null;
  const no = i.billingBuildingNo
    ? ` ${i.billingBuildingNo}${i.billingApartmentNo ? `/${i.billingApartmentNo}` : ''}`
    : '';
  return `${i.billingStreet}${no}, ${i.billingPostCode ?? ''} ${i.billingCity ?? ''}`.trim();
};

/**
 * Wspólny modal danych firmy: tworzenie i edycja. W trybie edycji zapis jest
 * odblokowywany checkboxem potwierdzenia — dane trafiają na faktury.
 */
const IntegratorFormModal: React.FC<{
  existing?: Integrator;
  onClose: () => void;
  onSaved: () => void;
}> = ({ existing, onClose, onSaved }) => {
  const isEdit = !!existing;
  const [form, setForm] = useState<CreateIntegratorRequest>({
    name: existing?.name ?? '',
    nip: existing?.nip ?? '',
    billingStreet: existing?.billingStreet ?? '',
    billingBuildingNo: existing?.billingBuildingNo ?? '',
    billingApartmentNo: existing?.billingApartmentNo ?? '',
    billingPostCode: existing?.billingPostCode ?? '',
    billingCity: existing?.billingCity ?? '',
    billingEmail: existing?.billingEmail ?? '',
  });
  const [confirmed, setConfirmed] = useState(!isEdit);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const set = (key: keyof CreateIntegratorRequest) =>
    (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!isValidNip(form.nip)) {
      setError('Nieprawidłowy NIP — podaj 10 cyfr z poprawną sumą kontrolną.');
      return;
    }
    if (!isValidPostCode(form.billingPostCode)) {
      setError('Nieprawidłowy kod pocztowy — wymagany format 00-000.');
      return;
    }

    setBusy(true);
    const payload: CreateIntegratorRequest = {
      ...form,
      name: form.name.trim(),
      nip: nipDigits(form.nip),
      billingStreet: form.billingStreet.trim(),
      billingBuildingNo: form.billingBuildingNo.trim(),
      billingApartmentNo: form.billingApartmentNo?.trim() || undefined,
      billingPostCode: form.billingPostCode.trim(),
      billingCity: form.billingCity.trim(),
      billingEmail: form.billingEmail?.trim() || undefined,
    };
    try {
      if (isEdit && existing) {
        await integratorApi.update(existing.id, payload);
      } else {
        await integratorApi.create(payload);
      }
      onSaved();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.response?.data?.detail
        || (isEdit ? 'Nie udało się zapisać zmian.' : 'Nie udało się utworzyć integratora.'));
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="modal-title">{isEdit ? `Edytuj dane — ${existing!.name}` : 'Nowy integrator'}</h2>
        <form onSubmit={submit}>
          {error && <div className="auth-error">{error}</div>}

          <div className="form-group">
            <label className="form-label">Nazwa firmy</label>
            <input className="form-input" value={form.name} onChange={set('name')} required autoFocus placeholder="np. Serwis Kowalski Sp. z o.o." />
          </div>

          <div className="form-group">
            <label className="form-label">NIP</label>
            <input className="form-input" value={form.nip} onChange={set('nip')} required inputMode="numeric" maxLength={13} placeholder="1234567890" />
          </div>

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <div className="form-group" style={{ flex: '3 1 180px' }}>
              <label className="form-label">Ulica</label>
              <input className="form-input" value={form.billingStreet} onChange={set('billingStreet')} required placeholder="Przemysłowa" />
            </div>
            <div className="form-group" style={{ flex: '1 1 90px' }}>
              <label className="form-label">Nr domu</label>
              <input className="form-input" value={form.billingBuildingNo} onChange={set('billingBuildingNo')} required placeholder="12" />
            </div>
            <div className="form-group" style={{ flex: '1 1 90px' }}>
              <label className="form-label">Nr lokalu</label>
              <input className="form-input" value={form.billingApartmentNo} onChange={set('billingApartmentNo')} placeholder="3" />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <div className="form-group" style={{ flex: '1 1 120px' }}>
              <label className="form-label">Kod pocztowy</label>
              <input className="form-input" value={form.billingPostCode} onChange={set('billingPostCode')} required inputMode="numeric" maxLength={6} placeholder="00-000" />
            </div>
            <div className="form-group" style={{ flex: '2 1 200px' }}>
              <label className="form-label">Miasto</label>
              <input className="form-input" value={form.billingCity} onChange={set('billingCity')} required />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">E-mail do faktur (opcjonalnie)</label>
            <input className="form-input" type="email" value={form.billingEmail} onChange={set('billingEmail')} placeholder="ksiegowosc@firma.pl" />
          </div>

          {isEdit ? (
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  style={{ marginTop: '0.2rem', flexShrink: 0 }}
                />
                <span>
                  <span style={{ fontWeight: 600 }}>Potwierdzam zmianę danych firmy</span>
                  <span className="item-meta" style={{ display: 'block', marginTop: '0.2rem' }}>
                    Te dane trafiają na faktury abonamentowe. Kolejna faktura zostanie wystawiona już na nowe dane.
                  </span>
                </span>
              </label>
            </div>
          ) : (
            <div className="item-meta" style={{ marginBottom: '0.75rem' }}>
              Komplet danych (NIP + adres) jest potrzebny do wystawiania faktur abonamentowych.
              Plan i cenę ustawisz w zakładce „Plany i faktury".
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Anuluj</button>
            <button type="submit" className="btn btn-primary" disabled={busy || !confirmed}>
              {busy ? 'Zapisuję…' : isEdit ? 'Zapisz zmiany' : 'Utwórz integratora'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/** Zaproszenie admina integratora w modalu (analogicznie do "Utwórz integratora"). */
const InviteAdminModal: React.FC<{
  integrators: Integrator[];
  onClose: () => void;
  onInvited: () => void;
}> = ({ integrators, onClose, onInvited }) => {
  const [selected, setSelected] = useState<Integrator | null>(null);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected) {
      setError('Wskaż integratora (wpisz NIP lub nazwę i wybierz z listy).');
      return;
    }
    setError('');
    setBusy(true);
    try {
      await integratorApi.invite(selected.id, email.trim(), firstName.trim(), lastName.trim());
      onInvited();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.response?.data?.detail || 'Nie udało się wysłać zaproszenia.');
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="modal-title">Zaproś admina integratora</h2>
        <form onSubmit={submit}>
          {error && <div className="auth-error">{error}</div>}

          <div className="form-group">
            <IntegratorAutocomplete
              integrators={integrators}
              value={selected}
              onChange={setSelected}
              label="Integrator (wpisz NIP lub nazwę)"
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">E-mail admina (będzie loginem)</label>
            <input className="form-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="admin@firma.pl" />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Imię</label>
              <input className="form-input" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Nazwisko</label>
              <input className="form-input" value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
          </div>

          <div className="item-meta" style={{ marginBottom: '0.75rem' }}>
            {selected
              ? <>Zaproszenie podpina konto pod: <strong>{integratorLabel(selected)}</strong></>
              : 'Zaproszony otrzyma mail z linkiem aktywacyjnym (ważny 7 dni) i rolą admina integratora.'}
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>Anuluj</button>
            <button type="submit" className="btn btn-primary" disabled={busy || !email.trim()}>
              {busy ? 'Wysyłanie…' : 'Zaproś admina'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const IntegratorCard: React.FC<{
  integrator: Integrator;
  invitations: Invitation[];
  onEdit: (i: Integrator) => void;
  onChanged: () => void;
  onInvitationsChanged: () => void;
  showToast: (kind: 'success' | 'error', msg: string) => void;
}> = ({ integrator, invitations, onEdit, onChanged, onInvitationsChanged, showToast }) => {
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoError, setLogoError] = useState('');
  const [invBusyId, setInvBusyId] = useState<string | null>(null);

  const resendInvite = async (inv: Invitation) => {
    setInvBusyId(inv.id);
    try {
      await integratorApi.resendInvitation(inv.id);
      showToast('success', `Zaproszenie do ${inv.email} wysłane ponownie (nowy link, ważny 7 dni).`);
      onInvitationsChanged();
    } catch (e: any) {
      showToast('error', e?.response?.data?.message || e?.response?.data?.detail
        || 'Nie udało się ponowić zaproszenia.');
    } finally { setInvBusyId(null); }
  };

  const cancelInvite = async (inv: Invitation) => {
    if (!window.confirm(`Anulować zaproszenie do ${inv.email}?`)) return;
    setInvBusyId(inv.id);
    try {
      await integratorApi.cancelInvitation(inv.id);
      showToast('success', `Zaproszenie do ${inv.email} anulowane.`);
      onInvitationsChanged();
    } catch (e: any) {
      showToast('error', e?.response?.data?.message || e?.response?.data?.detail
        || 'Nie udało się anulować zaproszenia.');
    } finally { setInvBusyId(null); }
  };

  const uploadLogo = async (file: File | undefined) => {
    if (!file) return;
    setLogoBusy(true);
    setLogoError('');
    try {
      await integratorApi.uploadLogo(integrator.id, file);
      onChanged();
    } catch (e: any) {
      // Bez komunikatu odrzucony plik (np. SVG — poza allowlistą) wygląda jak "nic się nie stało".
      setLogoError(e?.response?.data?.detail || e?.response?.data?.message
        || 'Nie udało się wgrać logo (dozwolone: PNG/JPG/GIF/WEBP/BMP, max 5 MB).');
    } finally { setLogoBusy(false); }
  };

  const removeLogo = async () => {
    setLogoBusy(true);
    setLogoError('');
    try {
      await integratorApi.deleteLogo(integrator.id);
      onChanged();
    } catch (e: any) {
      setLogoError(e?.response?.data?.detail || e?.response?.data?.message || 'Nie udało się usunąć logo.');
    } finally { setLogoBusy(false); }
  };

  const address = addressLabel(integrator);

  return (
    <div className="card" style={{ padding: '1.25rem', marginBottom: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0 }}>
          {integrator.logoUrl ? (
            <img
              src={integrator.logoUrl}
              alt={`Logo ${integrator.name}`}
              style={{
                width: 40, height: 40, flexShrink: 0, objectFit: 'contain',
                borderRadius: 6, background: '#fff', border: '1px solid var(--color-border)',
              }}
            />
          ) : (
            <span style={{
              width: 40, height: 40, flexShrink: 0, borderRadius: 6,
              background: 'var(--color-surface-alt)', border: '1px solid var(--color-border)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 600, color: 'var(--color-text-muted)',
            }}>
              {integrator.name[0]?.toUpperCase()}
            </span>
          )}
          <h3 style={{ fontFamily: 'var(--font-display)', textTransform: 'uppercase', letterSpacing: '0.01em', minWidth: 0 }}>
            {integrator.name}
          </h3>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <span className="eyebrow">{invitations.length} zaproszeń</span>
          <label className="btn btn-secondary btn-sm" style={{ cursor: logoBusy ? 'wait' : 'pointer', margin: 0 }}>
            {logoBusy ? 'Zapisuję…' : integrator.logoUrl ? 'Zmień logo' : 'Dodaj logo'}
            <input
              type="file"
              accept=".png,.jpg,.jpeg,.gif,.webp,.bmp"
              disabled={logoBusy}
              style={{ display: 'none' }}
              onChange={(e) => { uploadLogo(e.target.files?.[0]); e.target.value = ''; }}
            />
          </label>
          {integrator.logoUrl && (
            <button className="btn btn-secondary btn-sm" disabled={logoBusy} onClick={removeLogo}>
              Usuń logo
            </button>
          )}
          <button className="btn btn-secondary btn-sm" onClick={() => onEdit(integrator)}>Edytuj</button>
        </div>
      </div>

      {logoError && (
        <div className="auth-error" style={{ marginTop: '0.5rem' }}>{logoError}</div>
      )}

      <div style={{ marginTop: '0.375rem', fontSize: '0.8125rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        <span className="mono">{integrator.nip ? `NIP ${integrator.nip}` : 'brak NIP'}</span>
        {address && <span>{address}</span>}
        {integrator.billingEmail && <span className="mono">{integrator.billingEmail}</span>}
      </div>

      {invitations.length > 0 && (
        <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
          {invitations.map((inv) => (
            <div key={inv.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem', flexWrap: 'wrap' }}>
              <span className={statusBadge[inv.status]}>{statusLabel[inv.status]}</span>
              <span className="mono">{inv.email}</span>
              <span style={{ color: 'var(--color-text-muted)' }}>
                {inv.roleType === 'EMPLOYEE_ADMIN' ? 'admin'
                  : inv.roleType === 'CUSTOMER' ? `klient${inv.customerNip ? ` (NIP ${inv.customerNip})` : ''}`
                  : 'pracownik'}
              </span>
              {inv.status !== 'ACCEPTED' && (
                <span style={{ display: 'inline-flex', gap: '0.375rem', marginLeft: 'auto' }}>
                  <button
                    className="btn btn-secondary btn-sm"
                    disabled={invBusyId === inv.id}
                    onClick={() => resendInvite(inv)}
                    title="Nowy link aktywacyjny (stary przestaje działać)"
                  >
                    Ponów
                  </button>
                  <button
                    className="btn btn-danger btn-sm"
                    disabled={invBusyId === inv.id}
                    onClick={() => cancelInvite(inv)}
                  >
                    Anuluj
                  </button>
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Integrators: React.FC = () => {
  const { user } = useAuth();
  const [integrators, setIntegrators] = useState<Integrator[]>([]);
  const [invitationsByIntegrator, setInvitationsByIntegrator] = useState<Record<string, Invitation[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editing, setEditing] = useState<Integrator | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const { toast, showToast, dismissToast } = useToast();

  const load = useCallback(async () => {
    try {
      const { data } = await integratorApi.list();
      setIntegrators(data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Nie udało się pobrać integratorów.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Zaproszenia wszystkich integratorów jednym requestem (zamiast strzału per karta).
  useEffect(() => {
    integratorApi.allInvitations()
      .then(({ data }) => {
        const grouped: Record<string, Invitation[]> = {};
        data.forEach((inv) => {
          (grouped[inv.integratorId] = grouped[inv.integratorId] || []).push(inv);
        });
        setInvitationsByIntegrator(grouped);
      })
      .catch(() => { /* karty pokażą 0 zaproszeń */ });
  }, [refreshKey]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return integrators;
    return integrators.filter((i) =>
      i.name.toLowerCase().includes(q)
      || (i.nip || '').includes(q)
      || (i.billingCity || '').toLowerCase().includes(q));
  }, [integrators, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pageItems = filtered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  if (!user?.roles?.includes('ADMIN')) {
    return <Navigate to="/machines" replace />;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <h1 className="page-title" style={{ margin: 0 }}>Integratorzy</h1>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={() => setShowInviteModal(true)}>
            Zaproś admina integratora
          </button>
          <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
            Utwórz integratora
          </button>
        </div>
      </div>
      <p className="item-meta" style={{ marginBottom: '1rem' }}>
        Utwórz firmę-integratora z kompletem danych do faktury, potem zaproś jej administratora mailem —
        firmę wskazujesz wpisując NIP lub nazwę. Każdy integrator widzi tylko własne maszyny i zgłoszenia.
      </p>

      <div className="card" style={{ padding: '0.75rem 1.25rem', marginBottom: '1rem', display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          className="form-input"
          style={{ flex: '1 1 260px' }}
          value={search}
          placeholder="Szukaj po nazwie, NIP-ie lub mieście…"
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
        />
        <span className="item-meta">
          {filtered.length} {filtered.length === 1 ? 'firma' : 'firm'}
        </span>
      </div>

      {error && <div className="card" style={{ color: 'var(--color-danger)', marginBottom: '1rem' }}>{error}</div>}

      {loading ? (
        <div className="empty-state">Ładowanie…</div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">{search ? 'Brak firm pasujących do wyszukiwania' : 'Brak integratorów'}</div>
      ) : (
        <>
          {pageItems.map((i) => (
            <IntegratorCard
              key={i.id}
              integrator={i}
              invitations={invitationsByIntegrator[i.id] ?? []}
              onEdit={setEditing}
              onChanged={load}
              onInvitationsChanged={() => setRefreshKey((k) => k + 1)}
              showToast={showToast}
            />
          ))}

          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.75rem', marginTop: '1rem' }}>
              <button className="btn btn-secondary btn-sm" disabled={safePage === 0} onClick={() => setPage(safePage - 1)}>
                ← Poprzednia
              </button>
              <span className="item-meta pagination-info">strona {safePage + 1} z {totalPages}</span>
              <button className="btn btn-secondary btn-sm" disabled={safePage >= totalPages - 1} onClick={() => setPage(safePage + 1)}>
                Następna →
              </button>
            </div>
          )}
        </>
      )}

      {showCreateModal && (
        <IntegratorFormModal
          onClose={() => setShowCreateModal(false)}
          onSaved={() => { setShowCreateModal(false); load(); }}
        />
      )}
      {showInviteModal && (
        <InviteAdminModal
          integrators={integrators}
          onClose={() => setShowInviteModal(false)}
          onInvited={() => { setShowInviteModal(false); setRefreshKey((k) => k + 1); }}
        />
      )}
      {editing && (
        <IntegratorFormModal
          existing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      <Toast toast={toast} onDismiss={dismissToast} />
    </div>
  );
};

export default Integrators;
